import { api } from './api';

export interface OrgSettingsResponse {
  data: { settings?: unknown };
}

/**
 * One in-flight `GET /api/org` shared by every caller that asks while it is open.
 *
 * The COP reads this endpoint on mount for the altitude profile, and more than
 * one consumer can want it in the same tick. Two identical requests would waste a
 * round trip and - worse - could answer differently, with an admin's write
 * landing between them, leaving two parts of the same screen describing different
 * configurations.
 *
 * Only the PROMISE is shared, not the settled value. Once it settles the entry is
 * dropped, so a later mount fetches again. This is deliberately not a cache: an
 * org's settings can change under a session, and nothing here should decide how
 * long a stale copy is acceptable.
 *
 * It is also not shared across identities. A shared console is a real deployment
 * here (`manual-touch.ts` and `manual-layout.ts` both remember settings per
 * operator for that reason), and without `resetOrgSettings` on logout an entry
 * left open on a stalled connection would be handed to whoever logs in next -
 * giving them the previous operator's altitude ceiling and presets, which drive
 * the GO-TO bar and the command bar's takeoff altitude. The timeout below bounds
 * how long such an entry can exist at all.
 */
let inFlight: Promise<unknown> | null = null;

/** Bounds the shared entry. Long enough for a slow link, short enough that a
 *  hung request cannot pin one open across a login. */
const ORG_SETTINGS_TIMEOUT_MS = 8_000;

export function readOrgSettings(): Promise<unknown> {
  if (inFlight) return inFlight;

  const request = api
    .get<OrgSettingsResponse>('/api/org', {
      signal: AbortSignal.timeout(ORG_SETTINGS_TIMEOUT_MS),
    })
    .then((res) => res.data?.settings);

  inFlight = request;
  const release = () => { if (inFlight === request) inFlight = null; };
  request.then(release, release);

  return request;
}

/**
 * Drop the shared entry. Called when the authenticated identity changes, so no
 * request issued under one operator's token can answer another's mount.
 */
export function resetOrgSettings(): void {
  inFlight = null;
}
